import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { MessageView, UserView } from '@olmt/shared'
import {
  clearReaction,
  copyMessage,
  copyText,
  deleteMessageForEveryone,
  loadOlderMessages,
  markChatRead,
  openChat,
  openChatAt,
  pinMessage,
  reactToMessage,
  setActiveChat,
  startOrJoinCall,
  voteOnPoll,
} from '../lib/data'
import {
  canCopyMessage,
  canForwardMessage,
  canSelectForDelete,
  chatAvatarUrl,
  chatTitle,
  disappearingWindow,
  isPinnedMessage,
  messagePreview,
  myReaction,
  onlineMemberCount,
  peerReadId,
} from '../lib/chat'
import { formatDay, formatPresence } from '../lib/time'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { Avatar } from '../components/Avatar'
import { Composer } from '../components/Composer'
import { ForwardPicker } from '../components/ForwardPicker'
import { IconButton } from '../components/IconButton'
import { MessageBubble } from '../components/MessageBubble'
import { SearchField } from '../components/SearchField'
import { SearchResults } from '../components/SearchResults'
import { Spinner } from '../components/Spinner'
import { ArrowLeftIcon, PhoneIcon, PinIcon, PinOffIcon, SearchIcon, TimerIcon, VideoIcon } from '../components/Icons'

const NO_MESSAGES: MessageView[] = []

/**
 * The empty member list a chat the store has not loaded yet gives its bubbles (W10). One shared
 * array, so a thread that is still loading does not hand every bubble a new list on every render —
 * the bubble's mention scan is keyed on it.
 */
const NO_MEMBERS: UserView[] = []
const NO_TYPING: Record<string, number> = {}
const GROUP_WINDOW_MS = 5 * 60 * 1000

/** How long a jumped-to bubble stays ringed. Long enough to find it, short enough not to be a state. */
const HIGHLIGHT_MS = 2600

/** How long a copy's or a forward's answer stays on screen before the thread is itself again. */
const NOTICE_MS = 4000

type RenderedRow =
  | { kind: 'day'; key: string; at: number }
  | { kind: 'unread'; key: string }
  | {
      kind: 'message'
      key: string
      message: MessageView
      grouped: boolean
      showAuthor: boolean
      replyTo: MessageView | null
      /** The other side's cursor has passed this message (see `peerReadId`). */
      read: boolean
      /** This row *is* the chat's pinned message (W5): its menu offers Unpin, not Pin. */
      pinned: boolean
    }

export function ChatThreadPane() {
  const { chatId = '' } = useParams<{ chatId: string }>()
  const navigate = useNavigate()
  const t = useT()
  const me = useAuthStore((state) => state.user)
  const meId = me?.id ?? null
  const chat = useChatStore((state) => state.chats[chatId])
  const messages = useChatStore((state) => state.messages[chatId] ?? NO_MESSAGES)
  const hasMore = useChatStore((state) => state.hasMore[chatId] ?? false)
  const loaded = useChatStore((state) => state.loaded[chatId] ?? false)
  const typing = useChatStore((state) => state.typing[chatId] ?? NO_TYPING)
  const onlineCount = useChatStore((state) =>
    chat ? onlineMemberCount(chat, me?.id ?? null, state.presence) : 0,
  )
  /** The chat's one pinned message (W5), or null when the bar is down. */
  const pinnedMessage = chat?.pinnedMessage ?? null

  const [replyTo, setReplyTo] = useState<MessageView | null>(null)
  /** The message the composer is rewriting in place (W2); null is the normal compose state. */
  const [editing, setEditing] = useState<MessageView | null>(null)
  /**
   * The delete being confirmed (W2's dialog): one message from a bubble's menu, or the whole
   * selection (W4). The list is what the dialog and the requests are derived from.
   */
  const [deleting, setDeleting] = useState<MessageView[] | null>(null)
  const [deletingBusy, setDeletingBusy] = useState(false)
  /** W4 selection mode: the picked message ids, or null while the mode is off. */
  const [selection, setSelection] = useState<string[] | null>(null)
  /** The message ids the forward sheet is sending; the sheet is up while this is not null. */
  const [forwarding, setForwarding] = useState<string[] | null>(null)
  /** What the last action said — a copy and a forward both answer, and neither may do it silently. */
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  /** The pinned bar's unpin is in flight (W5): its control is held until the answer lands. */
  const [unpinning, setUnpinning] = useState(false)
  /** The thread's own search field is open (W3); results replace the thread while it is. */
  const [searching, setSearching] = useState(false)
  /** What the last jump is doing or could not do — the honest line under the header. */
  const [jumpNotice, setJumpNotice] = useState<'loading' | 'missed' | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  /** The highlight this pane has already scrolled to, so a re-render does not scroll twice. */
  const appliedHighlight = useRef<string | null>(null)
  /** Bumped by every jump and by every chat switch: an answer from an older one is stale. */
  const jumpSeq = useRef(0)
  const pendingJump = useChatStore((state) => state.pendingJump)
  const highlightId = useChatStore((state) => state.highlightId)

  useEffect(() => {
    if (!chatId) return
    setActiveChat(chatId)
    return () => setActiveChat(null)
  }, [chatId])

  useEffect(() => {
    if (!chatId) return
    setReplyTo(null)
    setEditing(null)
    setDeleting(null)
    setError(null)
    setSearching(false)
    setJumpNotice(null)
    // W4's mode belongs to the chat it was entered in: a selection of ids means nothing in the
    // chat we just left, and the forward sheet's targets would be answering the wrong question.
    setSelection(null)
    setForwarding(null)
    setNotice(null)
    setUnpinning(false)
    // Whatever jump was in flight belonged to the chat being left: its answer must not speak here.
    jumpSeq.current++
    // A jump belongs to the chat it targets: leaving for another one abandons it rather than
    // surprising the reader with it much later.
    const jump = useChatStore.getState().pendingJump
    if (jump && jump.chatId !== chatId) useChatStore.getState().clearJump()
    // A fresh chat starts at its newest page — unless a jump is already on its way to it, which
    // owns the scroll position instead.
    stickToBottom.current = useChatStore.getState().highlightId === null
    openChat(chatId).catch(() => setError(t('chat.couldNotLoadChat')))
  }, [chatId])

  /**
   * The jump a search hit asked for (W3). The pane that showed the result only *asked*: the thread
   * owns the history and the scroller, so the paging, the scroll and the notice all happen here.
   *
   * It waits for the thread's own first page: `openChatAt` pages with `prependMessages`, while a
   * second `openChat` in flight would replace the list with the newest page and drop those pages.
   */
  useEffect(() => {
    if (!pendingJump || pendingJump.chatId !== chatId || !loaded) return
    const { messageId } = pendingJump
    useChatStore.getState().clearJump()
    // The jump is one-shot, and consuming it re-runs this effect — so its own answer is *not*
    // cancelled by that re-run. A sequence number is what makes a newer jump (or a chat switch)
    // stale, and it is what keeps the notice from describing a jump the reader has left behind.
    const seq = ++jumpSeq.current
    setJumpNotice('loading')
    openChatAt(chatId, messageId)
      .then((outcome) => {
        if (jumpSeq.current !== seq) return
        setJumpNotice(outcome === 'too-old' ? 'missed' : null)
      })
      .catch(() => {
        if (jumpSeq.current !== seq) return
        setJumpNotice(null)
        setError(t('chat.couldNotLoadChat'))
      })
  }, [pendingJump, chatId, loaded, t])

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element) return
    // A jump owns the scroll position while it is landing: sticking to the bottom here would fight
    // the `scrollIntoView` that follows, and the thread would bounce. Read live rather than through
    // a dependency — the highlight *clearing* must not re-run this and scroll away from the very
    // message the reader just landed on.
    if (useChatStore.getState().highlightId !== null) {
      stickToBottom.current = false
      return
    }
    if (stickToBottom.current) element.scrollTop = element.scrollHeight
  }, [messages.length, chatId])

  // Closing the field brings the thread back — and the scroller with it, freshly mounted at the
  // top, which is not where the reader left it. Returning to the newest page is the one honest
  // answer, and a jump right after this one scrolls to its own message anyway.
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (searching || !element || !stickToBottom.current) return
    element.scrollTop = element.scrollHeight
  }, [searching])

  /**
   * Scroll the jumped-to message into view and ring it for a moment. The store's highlight is set
   * only once the message is in the list (`openChatAt`), so the row exists by the time this runs.
   */
  useEffect(() => {
    if (highlightId === null) {
      appliedHighlight.current = null
      return
    }
    if (appliedHighlight.current === highlightId) return
    const element = scrollRef.current?.querySelector<HTMLElement>(`[data-message-id="${highlightId}"]`)
    if (!element) {
      // Nothing to ring: the thread is not holding that message (a stale highlight). Dropping it
      // beats leaving a jump pending for the rest of the session.
      useChatStore.getState().setHighlight(null)
      return
    }
    appliedHighlight.current = highlightId
    element.scrollIntoView({ block: 'center' })
    const timer = window.setTimeout(() => {
      // The ring is gone, and with it the jump: the thread follows new messages again, which is
      // what it does without one. A jump owns the scroll only while it is landing.
      stickToBottom.current = true
      if (useChatStore.getState().highlightId === highlightId) useChatStore.getState().setHighlight(null)
    }, HIGHLIGHT_MS)
    return () => window.clearTimeout(timer)
  }, [highlightId])

  useEffect(() => {
    const newest = messages[messages.length - 1]
    if (!newest || newest.authorId === me?.id) return
    markChatRead(chatId, newest.id)
  }, [chatId, messages, me?.id])

  /**
   * Escape leaves selection mode (W4). Not while a dialog is up: the delete confirmation and the
   * forward sheet own Escape while they are the surface in front, and closing both at once would
   * throw the selection away under a dismissal the reader aimed at the dialog.
   */
  useEffect(() => {
    if (selection === null) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || deleting !== null || forwarding !== null) return
      setSelection(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selection, deleting, forwarding])

  // A copy and a forward are answered in words, and the words are not a state: the message shows
  // what happened, and a sentence left over the composer long after would be the only trace of it.
  useEffect(() => {
    if (notice === null) return
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS)
    return () => window.clearTimeout(timer)
  }, [notice])

  const rows = useMemo<RenderedRow[]>(() => {
    const byId = new Map(messages.map((message) => [message.id, message]))
    const unreadAfter = chat && chat.unreadCount > 0 ? chat.lastReadId : null
    // The other side's cursor: `ChatView.reads` for the seed and live `read` frames after that.
    const peerCursor = chat ? peerReadId(chat, meId) : null
    const out: RenderedRow[] = []
    let lastDay = ''
    let lastAuthor = ''
    let lastAt = 0
    let unreadPlaced = false
    for (const message of messages) {
      const day = formatDay(message.createdAt)
      if (day !== lastDay) {
        out.push({ kind: 'day', key: `day-${message.id}`, at: message.createdAt })
        lastDay = day
        lastAuthor = ''
      }
      const grouped =
        message.kind !== 'system' &&
        lastAuthor === message.authorId &&
        message.createdAt - lastAt < GROUP_WINDOW_MS
      out.push({
        kind: 'message',
        key: message.id,
        message,
        grouped,
        showAuthor: chat?.kind === 'group' && !grouped,
        replyTo: message.replyToId ? (byId.get(message.replyToId) ?? null) : null,
        // The same rule the native client's row builder applies: ids are UUIDv7, so "at or below
        // the cursor" is "read", and an optimistic copy is never read.
        read:
          message.authorId === meId &&
          !message.pending &&
          peerCursor !== null &&
          message.id <= peerCursor,
        pinned: isPinnedMessage(message, chat),
      })
      lastAuthor = message.authorId
      lastAt = message.createdAt
      if (unreadAfter && !unreadPlaced && message.id === unreadAfter) {
        out.push({ kind: 'unread', key: 'unread-divider' })
        unreadPlaced = true
      }
    }
    return out
  }, [messages, chat, meId])

  const typers = Object.keys(typing)
    .map((userId) => chat?.members.find((member) => member.id === userId)?.displayName ?? t('chat.someone'))
    .filter(Boolean)

  /**
   * The picked bubbles, in thread order — what the bar's three actions are derived from, so the
   * buttons and the bubbles can never disagree about what is selected. An id the thread no longer
   * holds (a tombstone arrived, a page replaced the list) simply drops out of the answer.
   */
  const selectedMessages = useMemo(
    () => messages.filter((message) => selection?.includes(message.id) ?? false),
    [messages, selection],
  )
  const canCopySelection = selectedMessages.length > 0 && selectedMessages.every(canCopyMessage)
  const canForwardSelection = selectedMessages.length > 0 && selectedMessages.every(canForwardMessage)
  const canDeleteSelection = canSelectForDelete(selectedMessages, meId)

  const loadOlder = async () => {
    if (loadingOlder) return
    setLoadingOlder(true)
    try {
      await loadOlderMessages(chatId)
    } finally {
      setLoadingOlder(false)
    }
  }

  // The composer has one purpose at a time: starting a reply leaves edit mode, and starting an
  // edit replaces a half-typed reply. Both chips are the composer's, not the bubble's.
  const startReply = (message: MessageView) => {
    setEditing(null)
    setReplyTo(message)
  }

  const startEdit = (message: MessageView) => {
    setReplyTo(null)
    setError(null)
    setEditing(message)
  }

  /**
   * W4 — selection mode is one state: `null` is the mode off, an array is the mode on with that many
   * picks. Entering it from a bubble picks that bubble, so the bar is never up over nothing; the
   * bar's Cancel, Escape and leaving the chat all end it.
   */
  const startSelection = (message: MessageView) => setSelection([message.id])

  const toggleSelection = (message: MessageView) =>
    setSelection((previous) => {
      if (previous === null) return previous
      return previous.includes(message.id)
        ? previous.filter((id) => id !== message.id)
        : [...previous, message.id]
    })

  /** The one answer either copy gets: it worked, or the browser refused and the reader is told. */
  const answerCopy = (copied: boolean) =>
    setNotice({ text: t(copied ? 'chat.copied' : 'chat.copyFailed'), tone: copied ? 'ok' : 'error' })

  const copyOne = async (message: MessageView) => answerCopy(await copyMessage(message))

  /**
   * The bar's Copy: every selected message has a body — the button is disabled otherwise — so the
   * clipboard gets one line per message and a pasted selection keeps its shape.
   */
  const copySelection = async () =>
    answerCopy(await copyText(selectedMessages.map((message) => message.body ?? '').join('\n')))

  const startForward = (message: MessageView) => setForwarding([message.id])

  const startForwardSelection = () => {
    if (selection !== null) setForwarding([...selection])
  }

  /**
   * W5 — pin a message, or take the chat's pin down (`null`). The response is the chat itself, and
   * its `pinnedMessage` is what raises or drops the bar, so nothing here guesses. A refusal is said
   * out loud on the same line a failed copy or forward uses (W4).
   */
  const setPinned = async (message: MessageView, pinned: boolean) => {
    setError(null)
    try {
      await pinMessage(message.chatId, pinned ? message.id : null)
    } catch {
      setNotice({ text: t(pinned ? 'chat.pinFailed' : 'chat.unpinFailed'), tone: 'error' })
    }
  }

  /** The bar's own unpin control: the same request, with the control held while it lands. */
  const unpinFromBar = async () => {
    if (!pinnedMessage) return
    setUnpinning(true)
    await setPinned(pinnedMessage, false)
    setUnpinning(false)
  }

  /**
   * W9 — one emoji's tap, from a chip under a bubble and from the menu's strip alike. The two
   * answers are the store's own state, read through `myReaction`: the emoji this member already
   * used is taken back, any other one replaces it (a second emoji on one member is what the api's
   * key does). The response carries the whole list and `lib/data.ts` applies it, so a client whose
   * socket is down still shows its chip; a refusal is said out loud on the same line as a failed
   * pin, never swallowed.
   */
  const react = async (message: MessageView, emoji: string) => {
    setError(null)
    try {
      if (myReaction(message, meId) === emoji) await clearReaction(message.chatId, message.id)
      else await reactToMessage(message.chatId, message.id, emoji)
    } catch {
      setNotice({ text: t('chat.reactFailed'), tone: 'error' })
    }
  }

  /**
   * W11 — one option's tap, from a row under a poll's question. The api owns the rule (a
   * single-choice poll replaces my answer, my own answer goes back, a multiple-choice one toggles
   * that option), so nothing here decides; the response carries the whole view and `lib/data.ts`
   * applies it, so a client whose socket is down still shows its own tally. A refusal is said out
   * loud on the same line as a failed reaction, never swallowed.
   */
  const vote = async (message: MessageView, optionIndex: number) => {
    setError(null)
    try {
      await voteOnPoll(message.chatId, message.id, optionIndex)
    } catch {
      setNotice({ text: t('poll.failed'), tone: 'error' })
    }
  }

  /**
   * Delete asks first: the dialog's confirm is the only path to `DELETE /api/messages/:id` (W2).
   * The failure reuses the thread's alert line; success leaves the bubble a tombstone and the
   * composer in its normal state, even if that message was the one being edited.
   *
   * Deleting a selection is not batched (docs/CONTRACT.md §4): one request per message, all of them
   * sent, so a refusal on one does not quietly leave the rest undeleted. The mode ends with the
   * requests — by their answer half its picks are tombstones, and a selection of tombstones has no
   * action left to offer.
   */
  const confirmDelete = async () => {
    if (!deleting || deletingBusy) return
    setDeletingBusy(true)
    try {
      const outcomes = await Promise.allSettled(
        deleting.map((message) => deleteMessageForEveryone(message.chatId, message.id)),
      )
      if (outcomes.some((outcome) => outcome.status === 'rejected')) setError(t('chat.deleteFailed'))
      if (deleting.some((message) => message.id === editing?.id)) setEditing(null)
    } finally {
      setDeleting(null)
      setDeletingBusy(false)
      setSelection(null)
    }
  }

  /**
   * The header's two call buttons. The flow is `lib/data.ts`'s `startOrJoinCall` — the call log's
   * rows go through the same one — and this only owns the thread's error line.
   */
  const callFromHeader = async (kind: 'audio' | 'video') => {
    if (!chat) return
    setError(null)
    try {
      await startOrJoinCall(chat.id, kind)
    } catch {
      setError(t('chat.couldNotStartCall'))
    }
  }

  const title = chat ? chatTitle(chat, meId) : t('chat.fallbackTitle')
  const online = onlineCount > 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="app-header flex items-center gap-2 border-b border-line bg-surface">
        <IconButton label={t('chat.backToChats')} className="md:hidden" onClick={() => void navigate('/')}>
          <ArrowLeftIcon size={22} />
        </IconButton>
        {searching ? (
          // The same field the list wears, scoped to this chat: the contract's `chatId` is the only
          // difference between the two searches (docs/CONTRACT.md §4 *Search*).
          <SearchField chatId={chatId} label={t('search.inThisChat')} onClose={() => setSearching(false)} />
        ) : chat ? (
          <>
            {/* The header's identity block is the way into the chat's details — the same door
                every messenger puts behind the name (rename, members, photo, leave). The whole
                block is one button, so the 40px avatar is a hit target too; `-ms-1 px-1` keeps the
                avatar on the header's own start edge while the hover fill gets its room. */}
            <button
              type="button"
              onClick={() => void navigate(`/chat/${chat.id}/details`)}
              className="-ms-1 flex min-w-0 flex-1 items-center gap-2 rounded-panel px-1 py-0.5 text-start transition-colors duration-quick hover:bg-surface2/60"
            >
              <Avatar name={title} path={chatAvatarUrl(chat, meId)} size={40} online={online} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-title font-semibold text-ink">
                  <bdi>{title}</bdi>
                </span>
                <span className="block truncate text-meta text-muted">
                  {/* The second line spends its room on both facts the client has: how big the room
                      is, and how many of them are here now (§10.7 item 2). A DM has no member count
                      worth printing — presence is the whole story there. */}
                  {chat.kind === 'group'
                    ? t('chat.groupSubtitle', {
                        members: t(chat.memberCount === 1 ? 'chat.memberCountOne' : 'chat.memberCountOther', {
                          count: chat.memberCount,
                        }),
                        online: onlineCount,
                      })
                    : formatPresence(online, null)}
                </span>
              </span>
              <span className="sr-only">{t('chat.chatDetails')}</span>
            </button>
            {/* The thread's own search (W3): the same control as the list's, scoped to this chat.
                It sits before the calls — a call is still the point of the product, and search is
                the quieter door. */}
            <IconButton label={t('search.inThisChat')} size={40} onClick={() => setSearching(true)}>
              <SearchIcon size={20} />
            </IconButton>
            {/* Calls are the point of the product, so the primary one is the only filled control in
                the header. Voice stays a plain button next to it rather than both being equal. */}
            <IconButton label={t('chat.videoCall')} variant="accent" size={40} onClick={() => void callFromHeader('video')}>
              <VideoIcon size={20} />
            </IconButton>
            <IconButton label={t('chat.voiceCall')} size={40} onClick={() => void callFromHeader('audio')}>
              <PhoneIcon size={20} />
            </IconButton>
          </>
        ) : (
          <span className="flex-1 text-ui text-muted">{error ?? t('chat.loading')}</span>
        )}
      </header>

      {error && (
        <p role="alert" className="border-b border-line bg-surface px-4 py-1.5 text-meta text-danger">
          {error}
        </p>
      )}

      {/* A jump is either still paging or could not reach its message. Both are said out loud: the
          second is the design's rule that a control which can only fail is not a control, and a
          jump that quietly landed at the wrong place would be worse. */}
      {jumpNotice && (
        <p className="flex items-center gap-2 border-b border-line bg-surface px-4 py-1.5 text-meta text-muted">
          {jumpNotice === 'loading' ? (
            <>
              {/* The spinner is the live region; the words beside it are the visible half of the
                  same sentence, not a second announcement. */}
              <Spinner size={14} label={t('chat.loadingMessages')} />
              {t('chat.loadingMessages')}
            </>
          ) : (
            <span role="status">{t('search.olderThanLoaded')}</span>
          )}
        </p>
      )}

      {/* W5 — the chat's one pinned message, under the header: the pin glyph, who wrote it and its
          words on one line, and the one control that takes the bar down. The strip itself is the
          jump, and it is W3's own machinery (`requestJump` → `openChatAt` → the brass ring) — a
          second way to land on a message would be a second set of bugs. A tombstone can never be
          the pin: the api clears the pin when the pinned message is deleted and broadcasts the bar
          coming down, so the line always has something to say. While the thread is searching the
          bar steps aside with the composer — results are the pane's content then (W3). */}
      {pinnedMessage && !searching && (
        <div className="flex items-center gap-1 border-b border-line bg-surface ps-3 pe-1">
          <button
            type="button"
            onClick={() => useChatStore.getState().requestJump(chatId, pinnedMessage.id)}
            className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-panel px-1 text-start transition-colors duration-quick hover:bg-surface2/60"
          >
            <PinIcon size={16} className="shrink-0 text-accent" />
            <span className="min-w-0 flex-1 truncate text-meta">
              {/* The bar's name for a reader who cannot see the glyph: what this strip is, then
                  whose message it is and what it says. */}
              <span className="sr-only">{t('chat.pinnedMessage')}{' '}</span>
              <span className="font-semibold text-accent">
                <bdi>
                  {pinnedMessage.authorId === meId
                    ? t('chat.you')
                    : (pinnedMessage.author?.displayName ?? t('chat.unknownAuthor'))}
                </bdi>
              </span>
              <span className="text-muted">: </span>
              <span className="text-muted">
                <bdi>{messagePreview(pinnedMessage, null)}</bdi>
              </span>
            </span>
          </button>
          <IconButton
            label={t('chat.unpin')}
            size={36}
            disabled={unpinning}
            onClick={() => void unpinFromBar()}
          >
            <PinOffIcon size={18} />
          </IconButton>
        </div>
      )}

      {/* W13 — the chat's timer, said quietly under the header. It is deliberately not the pinned
          bar's shape: no fill, muted ink, no glyph in brass, nothing to tap — a fact about the chat
          rather than a message to open. It steps aside while the thread searches, like the bar. */}
      {chat?.disappearingSeconds != null && !searching && (
        <p className="flex items-center gap-2 border-b border-line bg-app px-4 py-1.5 text-meta text-muted">
          <TimerIcon size={14} className="shrink-0" />
          {t('chat.disappearingBanner', { window: disappearingWindow(chat.disappearingSeconds) })}
        </p>
      )}

      {searching ? (
        <SearchResults
          onSelect={(hit) => {
            setSearching(false)
            useChatStore.getState().requestJump(hit.chat.id, hit.message.id)
          }}
        />
      ) : (
        <div ref={scrollRef} className="chat-scroll">
          <div className="room-column px-3 py-3">
            {hasMore && (
              <div className="flex justify-center pb-3">
                <button
                  type="button"
                  onClick={() => void loadOlder()}
                  className="inline-flex min-h-11 items-center rounded-full px-4 text-ui text-muted ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 hover:text-ink"
                >
                  {loadingOlder ? <Spinner size={16} label={t('chat.loadingOlderMessages')} /> : t('chat.loadOlderMessages')}
                </button>
              </div>
            )}

            {!loaded && messages.length === 0 && (
              <div className="flex justify-center py-8 text-muted">
                <Spinner label={t('chat.loadingMessages')} />
              </div>
            )}

            <ul className="flex flex-col">
              {rows.map((row) => {
                if (row.kind === 'day') {
                  return (
                    <li key={row.key} className="my-3 flex justify-center">
                      <span className="rounded-full bg-surface2/80 px-3 py-1 text-meta text-muted ring-1 ring-line ring-inset">
                        {formatDay(row.at)}
                      </span>
                    </li>
                  )
                }
                if (row.kind === 'unread') {
                  return (
                    <li key={row.key} className="my-3 flex items-center gap-2" aria-label={t('chat.unreadMessages')}>
                      <span className="h-px flex-1 bg-accent/40" />
                      <span className="text-meta font-semibold text-accent">{t('chat.unreadMessages')}</span>
                      <span className="h-px flex-1 bg-accent/40" />
                    </li>
                  )
                }
                return (
                  <MessageBubble
                    key={row.key}
                    message={row.message}
                    own={row.message.authorId === meId}
                    showAuthor={row.showAuthor}
                    grouped={row.grouped}
                    replyTo={row.replyTo}
                    meId={meId}
                    members={chat?.members ?? NO_MEMBERS}
                    read={row.read}
                    pinned={row.pinned}
                    highlighted={row.message.id === highlightId}
                    selecting={selection !== null}
                    selected={selection?.includes(row.message.id) ?? false}
                    onReply={startReply}
                    onEdit={startEdit}
                    onDelete={(message) => setDeleting([message])}
                    onToggleSelect={toggleSelection}
                    onCopy={(message) => void copyOne(message)}
                    onForward={startForward}
                    onStartSelect={startSelection}
                    onSetPinned={(message, pinned) => void setPinned(message, pinned)}
                    onReact={(message, emoji) => void react(message, emoji)}
                    onVote={(message, optionIndex) => void vote(message, optionIndex)}
                  />
                )
              })}
            </ul>

            {typers.length > 0 && (
              <p role="status" className="flex items-center gap-2 px-1 pt-3 text-meta text-accent">
                <span aria-hidden="true" className="flex items-end gap-0.5">
                  {[0, 1, 2].map((index) => (
                    <span
                      key={index}
                      className="typing-dot size-1.5 rounded-full bg-accent"
                      style={{ animationDelay: `${index * 0.15}s` }}
                    />
                  ))}
                </span>
                {t(typers.length === 1 ? 'chat.typingOne' : 'chat.typingMany', { names: typers.join(', ') })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* A copy and a forward both answer in words (W4): the success and the refusal are the same
          line, and a failure is an alert rather than a status — a copy the browser refused is not
          a passing remark. */}
      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`border-t border-line bg-surface px-4 py-1.5 text-meta ${
            notice.tone === 'error' ? 'text-danger' : 'text-muted'
          }`}
        >
          {notice.text}
        </p>
      )}

      {/* W4 — the bar that turns the thread into a selection. It sits above the composer rather
          than replacing it: the draft in the field is not the selection's to throw away. The count
          is a live region, so every tap on a bubble is heard as well as seen. */}
      {chat && !searching && selection !== null && (
        <div className="border-t border-line bg-surface">
          <div className="room-column flex flex-wrap items-center gap-x-1.5 gap-y-1 px-3 py-2">
            <span role="status" className="min-w-0 flex-1 truncate px-1 text-ui font-semibold text-ink">
              {t('chat.selectedCount', { count: selection.length })}
            </span>
            <button
              type="button"
              disabled={!canCopySelection}
              onClick={() => void copySelection()}
              className="min-h-11 rounded-full px-3.5 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-40"
            >
              {t('chat.copy')}
            </button>
            <button
              type="button"
              disabled={!canForwardSelection}
              onClick={startForwardSelection}
              className="min-h-11 rounded-full px-3.5 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-40"
            >
              {t('chat.forward')}
            </button>
            <button
              type="button"
              disabled={!canDeleteSelection}
              onClick={() => {
                if (canDeleteSelection) setDeleting(selectedMessages)
              }}
              className="min-h-11 rounded-full px-3.5 text-ui font-medium text-danger ring-1 ring-danger/40 ring-inset transition-colors duration-quick hover:bg-danger/15 disabled:opacity-40"
            >
              {t('chat.delete')}
            </button>
            <button
              type="button"
              onClick={() => setSelection(null)}
              className="min-h-11 rounded-full px-3.5 text-ui font-medium text-ink transition-colors duration-quick hover:bg-surface2"
            >
              {t('chat.cancelSelect')}
            </button>
          </div>
        </div>
      )}

      {chat && !searching && (
        <Composer
          chatId={chatId}
          members={chat.members}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
        />
      )}

      {forwarding && (
        <ForwardPicker
          messageIds={forwarding}
          onClose={() => setForwarding(null)}
          onSent={(count) => {
            setForwarding(null)
            // The mode ends with the forward: its picks are copies in other chats now, and the
            // thread confirms where they went in the words the reader will look for.
            setSelection(null)
            setNotice({
              text: t(count === 1 ? 'chat.forwardedToOne' : 'chat.forwardedTo', { count }),
              tone: 'ok',
            })
          }}
        />
      )}

      {deleting && (
        <DeleteMessageDialog
          count={deleting.length}
          busy={deletingBusy}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}

type DeleteMessageDialogProps = {
  /** One message from a bubble's menu, several from the selection bar (W4). */
  count: number
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * The "ask first" between the Delete item and `DELETE /api/messages/:id` (W2). The shape is the
 * app's other dialogs' — a scrim over the thread, a `surface` panel, Escape and the scrim itself
 * cancel — and its danger button is the revoke-device flow's red filled pill. Focus lands on the
 * panel, not on Delete, so the destructive action is never one stray Enter away.
 *
 * A selection (W4) is asked for by its count instead of W2's single-message sentence: "it is
 * deleted for everyone" says nothing about the four bubbles the bar is holding.
 */
function DeleteMessageDialog({ count, busy, onCancel, onConfirm }: DeleteMessageDialogProps) {
  const t = useT()
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      // Not while the delete is in flight: the dialog stays up until the answer lands, so its
      // result can never close a dialog the user opened afterwards.
      if (event.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-app/80 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-message-heading"
        aria-describedby="delete-message-body"
        tabIndex={-1}
        className="panel-enter w-full max-w-sm rounded-panel bg-surface p-4 outline-none shadow-lift ring-1 ring-line-strong"
      >
        <h2 id="delete-message-heading" className="text-title font-semibold text-ink">
          {t('chat.deleteConfirmTitle')}
        </h2>
        <p id="delete-message-body" className="mt-1.5 text-ui text-muted">
          {t(count > 1 ? 'chat.deleteSelected' : 'chat.deleteConfirmBody', { count })}
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-50"
          >
            {t('chat.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="press flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-ui font-semibold text-on-danger hover:brightness-110 disabled:opacity-50"
          >
            {busy && <Spinner size={16} label={t('chat.deleting')} />}
            {t('chat.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}
